import React from 'react';
import { formatPrice } from '../utils/formatters';
import { DISTRIBUTION_CURVES, MIN_TARGET_MULTIPLE, MAX_TARGET_MULTIPLE, MIN_TAKE_INITIALS_MULTIPLE } from '../utils/distributionCurves';

/**
 * Controlled form for exit strategy parameters. Used by ExitStrategyPlanner and ExitStrategyTemplateModal.
 *
 * value: { targetMultiple, baseSource, exitLevelsCount, exitLevelsSpread, takeInitialsEnabled, takeInitialsMultiple,
 *          hodlEnabled, hodlPercentage, advancedDistributionEnabled, distributionCurve, distributionFocus, distributionIntensity }
 * onChange: (field, value) => void
 * context: { selectedBasePrice, targetPrice, showBaseSourceToggle?, averageCostForToggle?, takeInitialsPrice?,
 *            minTakeInitialsMultiple?, maxTakeInitialsMultiple?, hodlDisplayText?, tokenTicker? }
 */
const ExitStrategySettingsForm = ({ value, onChange, context = {} }) => {
  const {
    selectedBasePrice = 0,
    targetPrice = 0,
    showBaseSourceToggle = true,
    takeInitialsPrice = 0,
    minTakeInitialsMultiple = MIN_TAKE_INITIALS_MULTIPLE,
    maxTakeInitialsMultiple = 10,
    hodlDisplayText,
  } = context;

  const minTargetMultiple = Math.max(
    MIN_TARGET_MULTIPLE,
    value.takeInitialsEnabled && value.takeInitialsMultiple
      ? parseFloat(String(value.takeInitialsMultiple)) + 0.5
      : MIN_TARGET_MULTIPLE
  );

  const handleChange = (field, val) => {
    onChange(field, val);
  };

  return (
    <div className="bg-white dark:bg-gray-800">
      {/* Target Price summary + Base Source toggle */}
      <div className="mb-2">
        <div className="flex items-start justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <div className="mb-1"><strong>Initial Price:</strong> {formatPrice(selectedBasePrice)}</div>
            <div><strong>Target Price:</strong> {formatPrice(targetPrice)}</div>
          </div>
          {showBaseSourceToggle && (
            <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600 text-xs">
              <button
                type="button"
                className={`px-2 py-1 whitespace-nowrap ${value.baseSource === 'avg' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                onClick={() => handleChange('baseSource', 'avg')}
              >
                Avg Buy
              </button>
              <button
                type="button"
                className={`px-2 py-1 border-l border-gray-300 dark:border-gray-600 ${value.baseSource === 'current' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                onClick={() => handleChange('baseSource', 'current')}
              >
                Current
              </button>
            </div>
          )}
          {!showBaseSourceToggle && (
            <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600 text-xs">
              <button type="button" className="px-2 py-1 bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed whitespace-nowrap" disabled>
                Current
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Target Multiple */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Target Multiple</label>
          <div className="flex items-center">
            <input
              type="number"
              step="0.1"
              min={minTargetMultiple}
              max={MAX_TARGET_MULTIPLE}
              className="w-20 border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-2 py-1 mr-1"
              value={value.targetMultiple}
              onChange={(e) => handleChange('targetMultiple', e.target.value)}
            />
            <span className="text-gray-600 dark:text-gray-400">x</span>
          </div>
        </div>
        <div>
          <input
            type="range"
            min={minTargetMultiple}
            max={MAX_TARGET_MULTIPLE}
            step="0.1"
            value={value.targetMultiple}
            onChange={(e) => handleChange('targetMultiple', e.target.value)}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">Conservative</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Aggressive</span>
          </div>
        </div>
      </div>

      {/* Exit Levels Count & Spread */}
      <div className="mb-6 dark:border-gray-700">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Number of Exit Levels</label>
          <div className="flex items-center text-gray-800 dark:text-gray-200 font-semibold">
            {value.exitLevelsCount}
            {value.takeInitialsEnabled && (
              <span className="text-xs ml-1 text-gray-500 dark:text-gray-400">(+1 Take Initials)</span>
            )}
          </div>
        </div>
        <div className="mb-4">
          <input
            type="range"
            min="1"
            max="7"
            step="1"
            value={value.exitLevelsCount}
            onChange={(e) => handleChange('exitLevelsCount', parseInt(e.target.value, 10))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">Fewer, Larger Sells</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">More, Smaller Sells</span>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Price Spread Between Levels</label>
            <div className="flex items-center text-gray-800 dark:text-gray-200 font-semibold">{value.exitLevelsSpread}%</div>
          </div>
          <input
            type="range"
            min="2"
            max="50"
            step="1"
            value={value.exitLevelsSpread}
            onChange={(e) => handleChange('exitLevelsSpread', parseInt(e.target.value, 10))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">Narrow Range</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Wide Range</span>
          </div>
        </div>

        {/* Advanced Distribution */}
        <div className="mt-4 border-t dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer mr-3">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={value.advancedDistributionEnabled}
                  onChange={() => handleChange('advancedDistributionEnabled', !value.advancedDistributionEnabled)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" />
              </label>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Advanced Distribution</span>
            </div>
          </div>
          {value.advancedDistributionEnabled && (
            <div className="mt-3 space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Distribution Curve Type</div>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(DISTRIBUTION_CURVES).map(([key, curveValue]) => (
                    <button
                      key={curveValue}
                      type="button"
                      onClick={() => handleChange('distributionCurve', curveValue)}
                      className={`py-1 px-2 text-xs rounded-md transition-colors ${
                        value.distributionCurve === curveValue
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {key === 'BELL' ? 'Bell Curve' : key === 'FRONT_LOADED' ? 'Front Loaded' : key === 'BACK_LOADED' ? 'Back Loaded' : 'Valley'}
                    </button>
                  ))}
                </div>
              </div>
              {value.distributionCurve !== DISTRIBUTION_CURVES.FRONT_LOADED && value.distributionCurve !== DISTRIBUTION_CURVES.BACK_LOADED && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Select Focus Point</label>
                    <div className="text-sm text-gray-800 dark:text-gray-200">
                      {value.distributionFocus < 35 ? 'Early Levels' : value.distributionFocus > 65 ? 'Later Levels' : 'Middle (Target) Levels'}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Earlier</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Target</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Later</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={value.distributionFocus}
                    onChange={(e) => handleChange('distributionFocus', parseInt(e.target.value, 10))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  />
                </div>
              )}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Curve Intensity</label>
                  <div className="text-sm text-gray-800 dark:text-gray-200">
                    {value.distributionIntensity < 25 ? 'Subtle' : value.distributionIntensity < 50 ? 'Moderate' : value.distributionIntensity < 75 ? 'Strong' : 'Maximum'}
                  </div>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Even</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Maximum Effect</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={value.distributionIntensity}
                  onChange={(e) => handleChange('distributionIntensity', parseInt(e.target.value, 10))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Take Initials */}
      <div className="mb-6 border-t dark:border-gray-700 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer mr-3">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={value.takeInitialsEnabled}
                onChange={() => handleChange('takeInitialsEnabled', !value.takeInitialsEnabled)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" />
            </label>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Take Initials Enabled</span>
          </div>
          {value.takeInitialsEnabled && (
            <div className="flex items-center">
              <input
                type="number"
                step="0.1"
                min={minTakeInitialsMultiple}
                max={maxTakeInitialsMultiple}
                className="w-20 border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-2 py-1 mr-1"
                value={value.takeInitialsMultiple}
                onChange={(e) => handleChange('takeInitialsMultiple', e.target.value)}
              />
              <span className="text-gray-600 dark:text-gray-400">x</span>
            </div>
          )}
        </div>
        {value.takeInitialsEnabled && (
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">{minTakeInitialsMultiple.toFixed(1)}x</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{Number(maxTakeInitialsMultiple).toFixed(1)}x</span>
            </div>
            <input
              type="range"
              min={minTakeInitialsMultiple}
              max={maxTakeInitialsMultiple}
              step="0.1"
              value={value.takeInitialsMultiple}
              onChange={(e) => handleChange('takeInitialsMultiple', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
            />
            <div className="flex justify-between items-center mt-2">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Take Initials Price:</strong> {formatPrice(takeInitialsPrice)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Multiple:</strong> {parseFloat(String(value.takeInitialsMultiple || 0)).toFixed(1)}x
              </div>
            </div>
          </div>
        )}
      </div>

      {/* HODL */}
      <div className="mb-6 border-t dark:border-gray-700 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer mr-3">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={value.hodlEnabled}
                onChange={() => handleChange('hodlEnabled', !value.hodlEnabled)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" />
            </label>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">HODL Amount Enabled</span>
          </div>
          {value.hodlEnabled && (
            <div className="flex items-center">
              <input
                type="number"
                step="1"
                min="1"
                max="99"
                className="w-20 border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-2 py-1 mr-1"
                value={value.hodlPercentage}
                onChange={(e) => handleChange('hodlPercentage', parseInt(e.target.value, 10) || 0)}
              />
              <span className="text-gray-600 dark:text-gray-400">%</span>
            </div>
          )}
        </div>
        {value.hodlEnabled && (
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">1%</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">99%</span>
            </div>
            <input
              type="range"
              min="1"
              max="99"
              step="1"
              value={value.hodlPercentage}
              onChange={(e) => handleChange('hodlPercentage', parseInt(e.target.value, 10) || 0)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
            />
            <div className="flex justify-between items-center mt-2">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <strong>HODL Amount:</strong> {value.hodlPercentage}% of holdings
              </div>
              {hodlDisplayText != null && (
                <div className="text-sm text-gray-600 dark:text-gray-400">{hodlDisplayText}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitStrategySettingsForm;
